"use client"

import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { Checkbox, Popover } from "@mui/material"
import { LobbyAvailableFilters } from "api"
import { useTranslations } from "next-intl"
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react"
import {
  CaseIcon,
  CheckboxIcon,
  CheckedCheckboxIcon,
  FilterIconNew,
  LocationIcon,
  NewStrokeIcon,
  NewText,
  SearchInput,
} from "ui"
import { jobFilter } from "utils"
import { CareerLobbyMessages } from "../Careers"
import FiltersModal from "./FiltersModal"
import { FilterState } from "./Jobs"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-self: flex-end;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 100%;
    flex-direction: row;
    align-items: start;
    gap: 10px;
    justify-content: center;
  }
  ${(props) => props.theme.breakpoints.down("md")} {
    position: absolute;
    top: calc(100% + 17px);
    inset-inline-end: 0px;
  }
`

const StyledWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  position: relative;
  ${(props) => props.theme.breakpoints.up("md")} {
    position: static;
  }
`

const StyledGrid = styled.div`
  display: flex;
  flex: 1 0 0;
  width: 100%;
  max-width: 1040px;
  min-height: 70px;
  justify-content: flex-start;
  align-items: stretch;
  border-radius: 12px;
  background: ${(props) => props.theme.colors.white};
  box-shadow: 0 0 16px 0 rgba(0, 38, 83, 0.1);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.down("md")} {
    display: none;
  }
`

const MobileFilterButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: none;
  align-self: flex-end;

  ${(props) => props.theme.breakpoints.down("md")} {
    display: flex;
  }
`

const StyledButton = styled.button<{ active: boolean; $isOpen?: boolean }>`
  border: none;
  background: ${({ $isOpen, theme }) =>
    $isOpen ? theme.colors.advantagesBg4rentMarketing : "transparent"};

  appearance: none;
  -webkit-appearance: none;
  display: flex;
  padding: 16px 24px;
  justify-content: flex-start;
  gap: 8px;
  flex: 1 0 0;
  min-width: 0;
  border-radius: 0;
  position: relative;
  font-size: 16px;
  box-sizing: border-box;
  cursor: pointer;

  &:focus-visible {
    outline-offset: -2px;
  }

  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: 16px;
    bottom: 16px;
    width: 1px;
    background-color: #e4e4e4;
  }

  @media (hover: none) {
    &:hover {
      font-weight: ${({ active }) => !active && "unset  "};
      background-color: ${({ active }) => !active && "transparent"};
      color: ${({ active }) => !active && "black"};
      box-shadow: ${({ active }) => !active && "none"};
      &::after {
        background-color: ${({ active, theme }) => !active && theme.colors.gray};
      }
    }
  }
`

const SearchButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 24px;
  height: 100%;
`

const SearchButton = styled.button<{ $canSubmit: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 24px;
  min-width: 160px;
  border: none;
  outline: none;
  border-radius: 8px;
  cursor: ${({ $canSubmit }) => ($canSubmit ? "pointer" : "not-allowed")};
  background-color: ${({ theme, $canSubmit }) =>
    $canSubmit ? theme.colors.blue4rentMarketing : theme.colors.gray4rentMarketing};
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
`

const LocationSymbol = styled(LocationIcon, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>`
  width: 16px;
  height: 16px;

  path {
    fill: ${(props) => props.$color};
  }
`

const CompanySymbol = styled(NewStrokeIcon, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>`
  width: 16px;
  height: 16px;

  path {
    stroke: ${(props) => props.$color};
  }
`

const ScopeSymbol = styled(CaseIcon, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>`
  width: 16px;
  height: 16px;

  path {
    stroke: ${(props) => props.$color};
    stroke-opacity: 1;
  }
`

const LabelContent = styled.div<{ $color?: string }>`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
`

const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  height: 24px;
`

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  min-width: 0;

  span {
    text-align: left;
  }
`

const DropdownContainer = styled.div`
  padding: 8px;
  max-height: 320px;
  overflow-y: auto;
  min-width: 260px;
`

const DropdownItem = styled.label`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  height: 40px;
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  background-color: ${(props) => props.theme.colors.white};
  cursor: pointer;
`

const DropdownHeader = styled(DropdownItem)`
  background-color: ${(props) => props.theme.colors.lightGrey};
  margin-bottom: 0;
`

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
  &.Mui-disabled {
    color: ${(props) => props.theme.colors.lightGrey};
  }
`

const StyledSearchInput = styled(SearchInput)`
  margin-bottom: 6px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin: 32px 0;
  }
`

const emptyState = {
  areaIds: [],
  companyDivisions: [],
  companyIds: [],
  jobScopeIds: [],
  orderIds: [],
  professionIds: [],
  searchText: "",
}

type ComparableKey = keyof Pick<
  FilterState,
  "areaIds" | "companyDivisions" | "companyIds" | "jobScopeIds" | "orderIds" | "professionIds"
>

const comparableKeys: ComparableKey[] = [
  "areaIds",
  "companyDivisions",
  "companyIds",
  "jobScopeIds",
  "orderIds",
  "professionIds",
]

const arraysEqual = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort((x, y) => x - y)
  const sortedB = [...b].sort((x, y) => x - y)
  return sortedA.every((value, idx) => value === sortedB[idx])
}

const filtersEqual = (a: FilterState, b: FilterState) =>
  comparableKeys.every((key) => arraysEqual(a[key] as number[], b[key] as number[])) &&
  (a.searchText?.trim() ?? "") === (b.searchText?.trim() ?? "")

type Props = {
  filters: LobbyAvailableFilters
  state: FilterState
  setState: Dispatch<SetStateAction<FilterState>>
  messages: CareerLobbyMessages
  companyDivisionId: number[]
}

type FilterKey = keyof Pick<FilterState, "areaIds" | "companyIds" | "professionIds">

type Option = { id: number; name: string }

type SegmentProps = {
  label: string
  options: Option[]
  filterKey: FilterKey
  state: FilterState
  setState: Dispatch<SetStateAction<FilterState>>
}

function SegmentFilter({ label, options, filterKey, state, setState }: SegmentProps) {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [maxLength, setMaxLength] = useState(25)
  const containerRef = useRef<HTMLDivElement>(null)
  const open = Boolean(anchorEl)

  const textBlack = theme.colors.newBlack

  const calculateMaxLength = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth
      // Approximate: ~7px per character for 14px font
      const charsPerPixel = containerWidth / 7
      setMaxLength(Math.max(10, Math.floor(charsPerPixel)))
    }
  }, [])

  useEffect(() => {
    calculateMaxLength()
    window.addEventListener("resize", calculateMaxLength)

    // Also recalculate after a short delay to ensure the component is fully rendered
    const timeoutId = setTimeout(calculateMaxLength, 100)

    return () => {
      window.removeEventListener("resize", calculateMaxLength)
      clearTimeout(timeoutId)
    }
  }, [calculateMaxLength])

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const allSelected = state[filterKey].length === options.length || state[filterKey].length === 0

  const selectAll = () => {
    setState((prevState) => ({
      ...prevState,
      [filterKey]: [],
    }))
  }

  const selectOne = (id: number) => {
    setState((prevState) => {
      const isSelected = prevState[filterKey].includes(id)
      if (isSelected) {
        return {
          ...prevState,
          [filterKey]: prevState[filterKey].filter((value: number) => value !== id),
        }
      }
      if (prevState[filterKey].length + 1 === options.length) {
        return {
          ...prevState,
          [filterKey]: [],
        }
      }
      return {
        ...prevState,
        [filterKey]: [...prevState[filterKey], id],
      }
    })
  }

  const getAllLabel = () => {
    if (filterKey === "professionIds") return "כל התחומים"
    if (filterKey === "companyIds") return "כל החברות"
    if (filterKey === "areaIds") return "כל האזורים"
    return "הצגת הכל"
  }

  const selectedNames = options
    .filter((opt) => state[filterKey].includes(opt.id))
    .map((opt) => opt.name)
    .join(" | ")

  return (
    <>
      <StyledButton
        active={selectedNames.length > 0}
        $isOpen={open}
        onClick={handleOpen}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <LabelContent $color={textBlack}>
          <TextColumn ref={containerRef}>
            <LabelRow>
              {filterKey === "areaIds" && <LocationSymbol $color={textBlack} aria-hidden />}
              {filterKey === "companyIds" && <CompanySymbol $color={textBlack} aria-hidden />}
              {filterKey === "professionIds" && <ScopeSymbol $color={textBlack} aria-hidden />}
              <NewText
                component="span"
                desktopFontSize={18}
                mobileFontSize={16}
                color={textBlack}
                desktopFontWeight={selectedNames.length > 0 || open ? 600 : 400}
              >
                {label}
              </NewText>
            </LabelRow>
            {selectedNames && (
              <NewText
                component="span"
                desktopFontSize={14}
                mobileFontSize={14}
                mobileLineHeight={"150%"}
                desktopLineHeight={"150%"}
                color={theme.colors.newBlack}
                title={selectedNames}
                style={{
                  width: "100%",
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedNames.length > maxLength
                  ? `${selectedNames.slice(0, maxLength)}...`
                  : selectedNames}
              </NewText>
            )}
          </TextColumn>
        </LabelContent>
      </StyledButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <DropdownContainer>
          <DropdownHeader htmlFor={`${filterKey}-all`}>
            <StyledCheckbox
              checked={allSelected}
              onClick={selectAll}
              checkedIcon={<CheckedCheckboxIcon color={theme.colors.blue4rentMarketing} />}
              icon={<CheckboxIcon />}
              id={`${filterKey}-all`}
              sx={{
                ":hover": {
                  backgroundColor: "transparent",
                },
              }}
            />
            <NewText
              component="span"
              desktopFontSize={16}
              mobileFontSize={14}
              color={allSelected ? theme.colors.darkBlue4rentMarketing : "black"}
              desktopFontWeight={allSelected ? 600 : 400}
            >
              {getAllLabel()}
            </NewText>
          </DropdownHeader>
          {options.map((option) => {
            const isSelected = state[filterKey].some((value: number) => value === option.id)
            return (
              <DropdownItem key={option.id} htmlFor={`${filterKey}-${option.id}`}>
                <StyledCheckbox
                  checked={isSelected && !allSelected}
                  onClick={() => {
                    selectOne(option.id)
                    jobFilter(option.name, filterKey, "קריירה פילטר")
                  }}
                  checkedIcon={<CheckedCheckboxIcon color={theme.colors.blue4rentMarketing} />}
                  icon={<CheckboxIcon />}
                  id={`${filterKey}-${option.id}`}
                />
                <NewText
                  component="span"
                  desktopFontSize={16}
                  mobileFontSize={14}
                  color={isSelected && !allSelected ? theme.colors.darkBlue4rentMarketing : "black"}
                  desktopFontWeight={isSelected && !allSelected ? 600 : 400}
                >
                  {option.name}
                </NewText>
              </DropdownItem>
            )
          })}
        </DropdownContainer>
      </Popover>
    </>
  )
}
function FiltersComponent({ filters, state, setState, messages, companyDivisionId }: Props) {
  const t = useTranslations("Career")
  const [pendingState, setPendingState] = useState<FilterState>(state)
  const [interimState, setInterimState] = useState<FilterState>(state)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setPendingState(state)
    setInterimState(state)
  }, [state])

  const handleClose = () => setOpen(false)

  const clearAll = () => {
    setInterimState(emptyState)
    setState(emptyState)
  }

  const trimmedSearch = pendingState.searchText?.trim() ?? ""
  const hasChanges = !filtersEqual(pendingState, state)
  const canSubmit = hasChanges

  const handleSearch = (allowWithoutFilters = false) => {
    if (!canSubmit && !allowWithoutFilters) return
    const nextState = { ...pendingState, searchText: trimmedSearch }
    setPendingState(nextState)
    jobFilter("חיפוש", "חיפוש", "קריירה פילטר")
    setState(nextState)
  }

  const handleSearchChange = (value: string) => {
    setPendingState((prev) => ({ ...prev, searchText: value }))
  }

  return (
    <StyledWrapper>
      <StyledSearchInput
        placeholder={messages.search}
        value={pendingState.searchText}
        onChange={handleSearchChange}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            handleSearch(true)
          }
        }}
        onIconClick={() => handleSearch(true)}
      />
      <StyledContainer>
        <MobileFilterButton
          onClick={() => {
            setInterimState(pendingState)
            setOpen(true)
          }}
        >
          <FilterIconNew width={40} height={40} />
        </MobileFilterButton>
        <StyledGrid role="region" aria-label={t("filterJobs")}>
          <SegmentFilter
            label={messages.jobType}
            options={filters.avialableProfessions as Option[]}
            filterKey="professionIds"
            state={pendingState}
            setState={setPendingState}
          />
          <SegmentFilter
            label={messages.location}
            options={filters.avialableAreas as Option[]}
            filterKey="areaIds"
            state={pendingState}
            setState={setPendingState}
          />
          {companyDivisionId.length === 0 && (
            <SegmentFilter
              label={messages.company}
              options={filters.avialableCompanies as Option[]}
              filterKey="companyIds"
              state={pendingState}
              setState={setPendingState}
            />
          )}
          <SearchButtonWrapper>
            <SearchButton
              type="button"
              $canSubmit={canSubmit}
              disabled={!canSubmit}
              onClick={() => handleSearch(false)}
            >
              {messages.searchButtonLabel}
            </SearchButton>
          </SearchButtonWrapper>
        </StyledGrid>
        <FiltersModal
          interimState={interimState}
          setInterimState={setInterimState}
          setState={setState}
          state={state}
          handleClose={handleClose}
          clearAll={clearAll}
          open={open}
          filters={filters}
          messages={messages}
          companyDivisionId={companyDivisionId}
        />
      </StyledContainer>
    </StyledWrapper>
  )
}

export default FiltersComponent
